import React, { useState } from 'react';
import { Wallet, ChevronDown, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { WalletType } from '../../types/wallet';
import { useWallet } from './WalletProvider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Alert, AlertDescription } from '../ui/alert';

interface WalletOption {
  type: WalletType;
  name: string;
  description: string;
  icon: string;
  available: boolean;
  comingSoon?: boolean;
}

export function WalletConnector() {
  const { connectWallet, disconnectWallet, isConnecting, isConnected, account, error } = useWallet();
  const [showError, setShowError] = useState(false);

  const handleConnect = async (type: WalletType) => {
    try {
      setShowError(false);
      await connectWallet(type);
    } catch (err) {
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    }
  };

  const handleDisconnect = async () => {
    await disconnectWallet();
  };

  // Format address for display
  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const walletOptions: WalletOption[] = [
    {
      type: WalletType.METAMASK,
      name: 'MetaMask',
      description: 'Connect with MetaMask wallet',
      icon: '🦊',
      available: typeof window !== 'undefined' && !!window.ethereum?.isMetaMask,
    },
    {
      type: WalletType.THOR_WALLET,
      name: 'THOR Wallet',
      description: 'Connect with THOR Wallet',
      icon: '⚡',
      available: true,
    },
    {
      type: WalletType.WALLETCONNECT,
      name: 'WalletConnect',
      description: 'Scan QR code with your mobile wallet',
      icon: '📱',
      available: true,
      comingSoon: true,
    },
  ];

  // If connected, show connected state
  if (isConnected && account) {
    return (
      <div className="relative">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="bg-green-600/20 border-green-500/50 hover:bg-green-600/30 text-green-400"
            >
              <div className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
              {formatAddress(account.address)}
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-72">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-500 dark:text-slate-400">Connected Wallet</p>
              <p className="font-mono text-sm mt-1 break-all">{account.address}</p>
            </div>

            <DropdownMenuItem
              onClick={() => {
                navigator.clipboard.writeText(account.address);
              }}
              className="cursor-pointer"
            >
              <span className="mr-2">📋</span>
              Copy Address
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={() => {
                window.open(`https://etherscan.io/address/${account.address}`, '_blank');
              }}
              className="cursor-pointer"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              View on Explorer
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={handleDisconnect}
              className="cursor-pointer text-red-500 hover:text-red-600"
            >
              <span className="mr-2">🔌</span>
              Disconnect
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className="relative">
      {showError && error && (
        <div className="absolute top-full right-0 mt-2 w-80 z-50">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            disabled={isConnecting}
            className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white border-0"
          >
            <Wallet className="mr-2 h-4 w-4" />
            {isConnecting ? 'Connecting...' : 'Connect Wallet'}
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-72">
          {walletOptions.map((wallet) => (
            <DropdownMenuItem
              key={wallet.type}
              disabled={!wallet.available || isConnecting || wallet.comingSoon}
              onClick={() => handleConnect(wallet.type)}
              className="flex items-center gap-3 p-4 cursor-pointer"
            >
              <span className="text-2xl">{wallet.icon}</span>
              <div className="flex-1">
                <div className="font-medium flex items-center gap-2">
                  {wallet.name}
                  {wallet.comingSoon && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-500">
                      Soon
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  {wallet.available ? wallet.description : 'Not installed'}
                </div>
              </div>
            </DropdownMenuItem>
          ))}

          <div className="p-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              By connecting a wallet, you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
